import React, { useState } from 'react';
import { Search, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { cn } from '@/lib/utils';
import type { Tag } from '@/types';
import { TAG_GROUP_ORDER } from '@/hooks/useTags';
import type { ToolTag } from '@/hooks/useToolsFilter';

interface GlobalFilterSearchProps {
  tagsByGroup: Record<string, Tag[]>;
  toolTags: ToolTag[];
  selectedTagIds: Set<string>;
  selectedToolIds: Set<string>;
  onToggleTag: (tagId: string) => void;
  onToggleTool: (toolId: string) => void;
}

const GlobalFilterSearch: React.FC<GlobalFilterSearchProps> = ({
  tagsByGroup,
  toolTags,
  selectedTagIds,
  selectedToolIds,
  onToggleTag,
  onToggleTool
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Get total count of all filters
  const totalFilters = Object.values(tagsByGroup).reduce((sum, tags) => sum + tags.length, 0) + toolTags.length;

  // Filter all tags and tools based on search query
  const getFilteredResults = () => {
    if (!searchQuery.trim()) return null;
    
    const query = searchQuery.toLowerCase();
    const results: { group: string; items: Array<{ id: string; name: string; type: 'tag' | 'tool' }> }[] = [];

    // Search through tag groups in order
    TAG_GROUP_ORDER.forEach(group => {
      if (group === 'Tools') {
        // Handle tools separately
        const matchingTools = toolTags.filter(t => t.name.toLowerCase().includes(query));
        if (matchingTools.length > 0) {
          results.push({
            group: 'Tools',
            items: matchingTools.map(t => ({ id: t.id, name: t.name, type: 'tool' as const }))
          });
        }
      } else {
        const tags = tagsByGroup[group] || [];
        const matchingTags = tags.filter(t => t.name.toLowerCase().includes(query));
        if (matchingTags.length > 0) {
          results.push({
            group,
            items: matchingTags.map(t => ({ id: t.id, name: t.name, type: 'tag' as const }))
          });
        }
      }
    });

    return results;
  };

  const filteredResults = getFilteredResults();

  const handleSelect = (item: { id: string; type: 'tag' | 'tool' }) => {
    if (item.type === 'tool') {
      onToggleTool(item.id);
    } else {
      onToggleTag(item.id);
    }
    setSearchQuery('');
  };

  const isSelected = (item: { id: string; type: 'tag' | 'tool' }) => {
    return item.type === 'tool' ? selectedToolIds.has(item.id) : selectedTagIds.has(item.id);
  };

  return (
    <div className="mb-4">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="outline" 
            className="w-full justify-start text-muted-foreground font-normal"
          >
            <Search className="mr-2 h-4 w-4" />
            Search all {totalFilters} filters...
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0" align="start">
          <Command>
            <CommandInput 
              placeholder="Search filters..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              {!searchQuery.trim() ? (
                <div className="p-4 text-sm text-muted-foreground text-center">
                  Type to search across all filter categories
                </div>
              ) : filteredResults && filteredResults.length === 0 ? (
                <CommandEmpty>No filters found.</CommandEmpty>
              ) : (
                filteredResults?.map(({ group, items }) => (
                  <CommandGroup key={group} heading={group}>
                    {items.slice(0, 5).map(item => (
                      <CommandItem
                        key={item.id}
                        value={`${group}-${item.name}`}
                        onSelect={() => handleSelect(item)}
                        className="flex items-center gap-2"
                      >
                        <div className={cn(
                          "flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                          isSelected(item) 
                            ? "bg-primary text-primary-foreground" 
                            : "opacity-50"
                        )}>
                          {isSelected(item) && <Check className="h-3 w-3" />}
                        </div>
                        <span className="flex-1 truncate">{item.name}</span>
                      </CommandItem>
                    ))}
                    {items.length > 5 && (
                      <div className="px-2 py-1 text-xs text-muted-foreground">
                        + {items.length - 5} more in {group}
                      </div>
                    )}
                  </CommandGroup>
                ))
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default GlobalFilterSearch;
